import { KEY as SOLAR_KEY } from 'components/tool/Editor/KeyboardShortcutsWarningModal/KeyBindings';
import { KEY as ROOF_KEY } from 'components/tool/Editor/KeyboardShortcutsWarningModalRoof/KeyBindings';
import { ALL_ACTIONS, DESIGN_MODES } from '../../constants';
import { isInHouseSimulationMode, isModeDefault } from '../../util';

export type Shortcut = { keys: string[]; label: string };
// wide = full-width card, others sit two per row
export type ShortcutSection = { title: string; hint?: string; wide?: boolean; shortcuts: Shortcut[] };
// what the toolbar buttons' visibility depends on
export type ToolbarState = { designType: DesignType | undefined; mode: DesignModes; allowedActions: string[]; edgeSetbacks: boolean };

const IS_MAC = /Mac/i.test(navigator.userAgent);
const MOD = IS_MAC ? '⌘' : 'Ctrl';
const ALT = IS_MAC ? '⌥' : 'Alt';

type Tool = { label: string; shown: boolean };

const can = (allowedActions: string[], ...actions: ALL_ACTIONS[]) => actions.some((action) => allowedActions.includes(action));

// `shown` mirrors each toolbar button's own condition (SolarToolEditOption, TreeEditModeBtn, Roof editor)
const solarTools = ({ mode, allowedActions: allowed, edgeSetbacks }: ToolbarState): Record<SOLAR_KEY, Tool> => ({
	[SOLAR_KEY.DRAW]: { label: 'Draw Facet', shown: can(allowed, ALL_ACTIONS.EDIT_ROOF_SEGMENTS) },
	[SOLAR_KEY.EDIT]: { label: 'Edit Facets', shown: can(allowed, ALL_ACTIONS.UPDATE_FACETS_COORDS) },
	[SOLAR_KEY.ORIENTATION]: { label: 'Change Orientation', shown: can(allowed, ALL_ACTIONS.ROTATE_ROOF_SEGMENTS) },
	[SOLAR_KEY.DELETE_FACET]: { label: 'Delete Facet', shown: can(allowed, ALL_ACTIONS.EDIT_ROOF_SEGMENTS) },
	[SOLAR_KEY.RECALCULATE_LAYOUT]: { label: 'Recalculate Layout', shown: can(allowed, ALL_ACTIONS.RESET_LAYOUT) },
	[SOLAR_KEY.EDIT_OBSTRUCTION]: {
		label: 'Edit Obstructions',
		shown: can(allowed, ALL_ACTIONS.ADD_OBSTRUCTIONS, ALL_ACTIONS.DELETE_OBSTRUCTIONS),
	},
	[SOLAR_KEY.EDIT_EDGES]: { label: 'Edit Edges', shown: edgeSetbacks && can(allowed, ALL_ACTIONS.UPSERT_ROOF_SEGMENT_EDGES) },
	[SOLAR_KEY.ADJUST_SHADING]: isInHouseSimulationMode(mode)
		? { label: 'Edit Trees', shown: can(allowed, ALL_ACTIONS.REMOVE_PANELS) }
		: { label: 'Adjust Shading', shown: isModeDefault(mode) && can(allowed, ALL_ACTIONS.REMOVE_ROOF_SEGMENT_SHADING) },
});

const roofTools = ({ allowedActions: allowed }: ToolbarState): Record<ROOF_KEY, Tool> => ({
	[ROOF_KEY.DRAW]: { label: 'Draw Facet', shown: can(allowed, ALL_ACTIONS.EDIT_ROOF_SEGMENTS) },
	[ROOF_KEY.EDIT]: { label: 'Edit Facets', shown: can(allowed, ALL_ACTIONS.UPDATE_FACETS_COORDS) },
	[ROOF_KEY.EDIT_EDGES]: { label: 'Edit Edges', shown: can(allowed, ALL_ACTIONS.UPSERT_ROOF_SEGMENT_EDGES) },
	[ROOF_KEY.DELETE_FACET]: { label: 'Delete Facet', shown: can(allowed, ALL_ACTIONS.EDIT_ROOF_SEGMENTS) },
});

const toolShortcuts = (tools: Record<string, Tool>): Shortcut[] => Object.entries(tools)
	.filter(([, { shown }]) => shown)
	.sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
	.map(([key, { label }]) => ({ keys: [key], label }));

export function getShortcutSections(state: ToolbarState): ShortcutSection[] {
	const isRoof = state.designType === 'ROOF';

	return [
		{
			title: 'Tools',
			hint: 'Edit mode · press the number shown on the toolbar button',
			wide: true,
			shortcuts: toolShortcuts(isRoof ? roofTools(state) : solarTools(state)),
		},
		{
			title: 'Drawing',
			hint: 'While drawing a facet',
			wide: true,
			shortcuts: [
				{ keys: ['Shift'], label: 'Guide' },
				{ keys: ['Shift', 'S'], label: 'Snap' },
			],
		},
		{
			title: 'View & navigation',
			shortcuts: [
				{ keys: [MOD], label: 'Pan (hold & drag)' },
				// { keys: [ALT], label: 'Rotate map (hold & drag)' },
				// 3D button is hidden in NREL manual (solar and roof)
				...(state.mode !== DESIGN_MODES.NEARMAP_NREL_MANUAL ? [{ keys: [ALT], label: 'Rotate 3D view (hold & drag)' }] : []),
				{ keys: ['H'], label: 'Map only' },
				// Irradiance button: solar + default modes only
				...(!isRoof && isModeDefault(state.mode) ? [{ keys: ['I'], label: 'Irradiance' }] : []),
			],
		},
		{
			title: 'General',
			shortcuts: [
				{ keys: ['Esc'], label: 'Cancel / exit' },
				{ keys: ['Y'], label: 'Confirm in popups' },
				{ keys: ['N'], label: 'Cancel in popups' },
				{ keys: [MOD, 'Z'], label: 'Undo' },
				{ keys: [MOD, 'Shift', 'Z'], label: `Redo (or ${MOD} + Y)` },
			],
		},
	].filter(({ shortcuts }) => shortcuts.length);
}
